import { useEffect } from 'react';
import { useFinancieelStore } from '../../stores';
import type { OmzetRegel } from '../../stores/financieel-store';

const UPSELL_PRODUCTS = [
  { label: 'Ondertapijt', matches: ['ondertapijt'] },
  { label: 'Vlekverwijderaar', matches: ['vlekkenbaas', 'vlekkendoekje'] },
  { label: 'Randen plakken', matches: ['randen plakken'] },
];

interface MonthRate {
  month: string;
  rate: number; // percentage
  count: number;
}

function calculateUpsellTrends(
  trendsData: OmzetRegel[],
  matches: string[],
): MonthRate[] {
  // Group by month
  const months = new Map<string, { count: number; totalOrders: number }>();

  for (const row of trendsData) {
    if (!months.has(row.month)) {
      months.set(row.month, { count: 0, totalOrders: row.total_orders || 0 });
    }
    const entry = months.get(row.month)!;
    if (entry.totalOrders === 0 && row.total_orders) {
      entry.totalOrders = row.total_orders;
    }

    // Match any of the product names (case-insensitive partial match)
    const name = row.product_name?.toLowerCase() || '';
    if (matches.some(m => name.includes(m))) {
      entry.count += row.invoice_count;
    }
  }

  return Array.from(months.entries())
    .map(([month, { count, totalOrders }]) => ({
      month,
      rate: totalOrders > 0 ? Math.round(count / totalOrders * 1000) / 10 : 0,
      count,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

function TrendIndicator({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (Math.abs(diff) < 0.2) {
    return <span className="text-gray-400 text-sm">&rarr;</span>;
  }
  if (diff > 0) {
    return <span className="text-emerald-600 text-sm">&uarr; +{diff.toFixed(1)}pp</span>;
  }
  return <span className="text-red-600 text-sm">&darr; {diff.toFixed(1)}pp</span>;
}

function MiniBar({ rates, maxRate }: { rates: MonthRate[]; maxRate: number }) {
  return (
    <div className="flex gap-1">
      {rates.map((r) => (
        <div key={r.month} className="flex-1 flex flex-col items-center min-w-0">
          <div className="w-full flex items-end justify-center h-8">
            <div
              className="w-full max-w-6 bg-indigo-400 rounded-t"
              style={{ height: maxRate > 0 ? `${Math.max(2, (r.rate / maxRate) * 32)}px` : '2px' }}
              title={`${r.month}: ${r.rate}% (${r.count})`}
            />
          </div>
          <span className="text-[10px] text-gray-400 mt-1 truncate">
            {formatShortMonth(r.month)}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatShortMonth(month: string): string {
  const [, m] = month.split('-');
  const months = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  return months[parseInt(m!) - 1] || m!;
}

export function UpsellTrends() {
  const { trendsData, loadTrends } = useFinancieelStore();

  useEffect(() => {
    loadTrends(6);
  }, [loadTrends]);

  if (trendsData.length === 0) {
    return (
      <div className="bg-white rounded-card shadow-card p-6">
        <h2 className="text-lg font-semibold text-main mb-4">Upsell Trends</h2>
        <p className="text-sm text-gray-400">Nog geen historische data beschikbaar. Data wordt opgebouwd bij het bekijken van maanden.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <h2 className="text-lg font-semibold text-main mb-4">Upsell Trends</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {UPSELL_PRODUCTS.map(product => {
          const rates = calculateUpsellTrends(trendsData, product.matches);
          if (rates.length === 0) return null;

          const currentRate = rates[rates.length - 1]?.rate || 0;
          const previousRate = rates.length >= 2 ? rates[rates.length - 2]!.rate : currentRate;
          const maxRate = Math.max(...rates.map(r => r.rate), 1);

          return (
            <div key={product.label} className="p-3 border border-gray-100 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-main">{product.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums">{currentRate}%</span>
                  <TrendIndicator current={currentRate} previous={previousRate} />
                </div>
              </div>
              <MiniBar rates={rates} maxRate={maxRate} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
